/**
 * The strip — `docs/new-request.md` §10.5, Boards 7 A, 8 F, 13 B, 15 C.
 *
 * Ten day cells: the weekday (*Today* for the first), the date, the
 * straight-line distance from the new place to the person's nearest stop that
 * day with its dot, and the fit word — *n fits* / *n fit*, *full* (dimmed, the
 * distance still shown), *open*, *off*. The selected day wears the green ring.
 * The server measured every cell; this draws them.
 */
import { milesWords, weekdayShort, type StripCell } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { TONE_FILL, TONE_TEXT } from './tones.js'

/** *1 fits*, *2 fit*, *full*, *open*, *off* — the boards' words. */
export function fitWords(cell: StripCell): string {
  switch (cell.fit.kind) {
    case 'fits': return cell.fit.count === 1 ? '1 fits' : `${cell.fit.count} fit`
    case 'full': return 'full'
    case 'open': return 'open'
    case 'off': return 'off'
  }
}

const fitTone = (cell: StripCell): string =>
  cell.fit.kind === 'fits' && cell.fit.count > 1 ? 'text-accent-dark'
    : cell.fit.kind === 'full' ? 'text-text-faint' : 'text-text-muted'

const dimmed = (cell: StripCell): boolean => cell.fit.kind === 'off' || cell.fit.kind === 'full'

/** The distance line: *4.3 mi*, *no stops*, or *off*. Absent without a place. */
function distance(cell: StripCell, hasPlace: boolean): { text: string; tone: StripCell['tone'] } | null {
  if (cell.fit.kind === 'off') return { text: 'off', tone: 'far' }
  if (!hasPlace) return null
  if (cell.nearest === null) return { text: 'no stops', tone: 'far' }
  return { text: milesWords(cell.nearest), tone: cell.tone }
}

const dayOfMonth = (date: string): number => Number(date.slice(8, 10))

export function Strip({ cells, today, selected, hasPlace, onPick, width }: {
  cells: StripCell[]
  today: string
  selected: string | null
  hasPlace: boolean
  onPick: (date: string) => void
  /** 524 in the aside, 584 in iPad portrait. */
  width?: number
}) {
  return (
    <div className="flex gap-[3px]" style={width ? { width } : undefined} role="listbox" aria-label="Days">
      {cells.map((cell) => {
        const shown = distance(cell, hasPlace)
        const on = cell.date === selected
        return (
          <button
            key={cell.date}
            type="button"
            role="option"
            aria-selected={on}
            data-day={cell.date}
            onClick={() => onPick(cell.date)}
            className={cn('flex min-w-0 flex-1 basis-0 flex-col items-center gap-0.5 rounded-card border-[1.5px]',
              'pb-2 pt-[7px] leading-[normal]',
              on ? 'border-accent bg-surface shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
                : 'border-transparent hover:bg-surface',
              dimmed(cell) && 'opacity-[.55]')}
          >
            <span className="text-[10.5px] font-semibold uppercase tracking-[.04em] text-text-muted">
              {cell.date === today ? 'Today' : weekdayShort(cell.date)}
            </span>
            <b className="text-[15px] font-bold leading-[1.1] text-text">{dayOfMonth(cell.date)}</b>
            {shown
              ? (
                <span className={cn('inline-flex items-center gap-[3px] whitespace-nowrap text-[10.5px] font-bold',
                  TONE_TEXT[shown.tone])}>
                  <i className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', TONE_FILL[shown.tone])} />
                  {shown.text}
                </span>
              )
              : <span className="h-[13px]" aria-hidden />}
            <span className={cn('text-[10px] font-semibold', fitTone(cell))}>{fitWords(cell)}</span>
          </button>
        )
      })}
    </div>
  )
}

/** The phone's sideways strip: 58 × 80 cells, no dot, inside the gutter (Board 15 C). */
export function PhoneStrip({ cells, today, selected, hasPlace, onPick }: {
  cells: StripCell[]
  today: string
  selected: string | null
  hasPlace: boolean
  onPick: (date: string) => void
}) {
  return (
    <div className="no-scrollbar flex gap-1.5 overflow-x-auto" role="listbox" aria-label="Days">
      {cells.map((cell) => {
        const shown = distance(cell, hasPlace)
        const on = cell.date === selected
        return (
          <button
            key={cell.date}
            type="button"
            role="option"
            aria-selected={on}
            data-day={cell.date}
            onClick={() => onPick(cell.date)}
            className={cn('flex w-[58px] flex-none flex-col items-center gap-0.5 rounded-[12px] border-[1.5px] py-[7px]',
              'leading-[normal]',
              on ? 'border-accent bg-surface' : 'border-transparent',
              cell.fit.kind === 'off' && 'opacity-[.55]')}
          >
            <span className="text-[10.5px] font-semibold uppercase text-text-muted">
              {cell.date === today ? 'Today' : weekdayShort(cell.date)}
            </span>
            <b className="text-[16px] font-bold text-text">{dayOfMonth(cell.date)}</b>
            {shown
              ? <span className={cn('text-[10.5px] font-bold', TONE_TEXT[shown.tone])}>{shown.text}</span>
              : <span className="h-[13px]" aria-hidden />}
            <span className="text-[10px] text-text-muted">{fitWords(cell)}</span>
          </button>
        )
      })}
    </div>
  )
}
